// Carriers list page — FE-SCRM-030.
//
// Owns ``/dashboard/carriers``. Read access is operational-wide (any
// authenticated entity member). Filter state lives in the URL query
// string so deep links share the user's view and back-button nav
// restores it. The list endpoint takes ``?q=`` (server-side
// substring on name), ``?carrier_type=``, ``?status=``; the BE
// aggregates ``contact_count`` + ``last_touchpoint_at`` per row so
// the list can surface relationship-health signals without N+1.

import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Toast } from "@/design-system";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";
import { usePermissions } from "@/features/auth";

import { CarrierFormModal } from "./CarrierFormModal";
import { CarriersListFilters } from "./CarriersListFilters";
import { CarriersListTable } from "./CarriersListTable";
import { useCarrierListQuery, useCreateCarrierMutation } from "./hooks";
import {
  parseFiltersFromSearch,
  serializeCarriersListUrl,
} from "./urlState";
import type {
  CarrierListFilters as Filters,
  CarrierWriteInput,
} from "./types";


export function CarriersListPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const canManageCarriers = permissions.is_admin_or_owner;

  const filters = useMemo<Filters>(
    () => parseFiltersFromSearch(search),
    [search],
  );

  const updateUrl = useCallback(
    (next: Filters) => {
      navigate(serializeCarriersListUrl(next), { replace: false });
    },
    [navigate],
  );

  const query = useCarrierListQuery(filters);
  const [addOpen, setAddOpen] = useState(false);
  const [toast, setToast] = useState<
    | null
    | { variant: "success" | "error"; title: string; description?: string }
  >(null);
  const createMutation = useCreateCarrierMutation();

  function submitNewCarrier(values: CarrierWriteInput) {
    createMutation.mutate(values, {
      onSuccess: (created) => {
        setAddOpen(false);
        setToast({
          variant: "success", title: `${created.name} added`,
        });
      },
    });
  }

  const hasActiveFilters = Boolean(
    filters.q
      || filters.carrier_type
      || (filters.status && filters.status !== "active"),
  );
  const hasNoRows = query.data && query.data.rows.length === 0;
  const isInitialLoad = query.isLoading && !query.data;

  return (
    <div data-testid="carriers-list-page" className="flex flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-text-primary">
            Carriers
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-text-secondary">
            Insurance carriers your brokerage works with.
          </p>
        </div>
        {canManageCarriers && (
          <button
            type="button"
            data-testid="carriers-list-add-button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1 rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse transition-fast ease-out hover:opacity-90"
          >
            + Add carrier
          </button>
        )}
      </header>

      <CarriersListFilters filters={filters} onChange={updateUrl} />

      {isInitialLoad ? (
        <DashboardLoadingState variant="table" rows={6} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load carriers"
          description="Refresh to retry, or check the network connection."
          retry={() => query.refetch()}
        />
      ) : query.data && query.data.rows.length > 0 ? (
        <CarriersListTable
          rows={query.data.rows}
          isRefreshing={query.isFetching}
        />
      ) : hasNoRows ? (
        <EmptyState
          hasActiveFilters={hasActiveFilters}
          onClear={() => updateUrl({ status: "active" })}
        />
      ) : null}

      <CarrierFormModal
        open={addOpen}
        mode="create"
        isSubmitting={createMutation.isPending}
        errorMessage={
          createMutation.isError
            ? formatBackendError(createMutation.error)
            : null
        }
        onClose={() => setAddOpen(false)}
        onSubmit={submitNewCarrier}
      />

      {toast && (
        <Toast
          variant={toast.variant}
          title={toast.title}
          description={toast.description}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}


function EmptyState({
  hasActiveFilters, onClear,
}: { hasActiveFilters: boolean; onClear: () => void }) {
  if (hasActiveFilters) {
    return (
      <DashboardEmptyState
        title="No carriers match your filters"
        description="Adjust the filters above or clear them to see every carrier in your brokerage."
        action={
          <button
            type="button"
            data-testid="carriers-list-clear-filters"
            onClick={onClear}
            className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
          >
            Clear filters
          </button>
        }
      />
    );
  }
  return (
    <DashboardEmptyState
      title="No carriers yet"
      description="The default carrier library should have been seeded automatically. Contact support if you don't see carriers here."
    />
  );
}


function formatBackendError(err: unknown): string {
  if (err && typeof err === "object" && "body" in err) {
    const body = (err as { body?: { detail?: string } }).body;
    if (body?.detail) {
      switch (body.detail) {
        case "duplicate_carrier_name":
          return "A carrier with this name already exists.";
        case "name_required":
          return "Name is required.";
        case "carrier_type_invalid":
          return "Carrier type is invalid.";
        case "forbidden":
          return "You don't have permission to do that.";
        default:
          return body.detail.replaceAll("_", " ");
      }
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return "Something went wrong. Please try again.";
}
